import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { combineLatest, map } from 'rxjs';
import { TopBarComponent } from '../../../shared/components/top-bar/top-bar.component';
import { FinanceStateService } from '../../services/finance.state-service';
import { RepaymentChartComponent } from '../../components/repayment-chart/repayment-chart.component';
import { OverallProgressChartComponent } from '../../components/overall-progress-chart/overall-progress-chart.component';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    TopBarComponent,
    RepaymentChartComponent,
    OverallProgressChartComponent,
  ],
  templateUrl: './dashboard-page.component.html',
  styleUrl: './dashboard-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardPageComponent implements OnInit {
  data$ = combineLatest({
    assets: this.financeState.assets$,
    partners: this.financeState.partners$,
  }).pipe(
    map(({ assets, partners }) => {
      const totalContributed = assets.reduce((sum, asset) => sum + asset.paid, 0);
      const totalTarget = assets.reduce((sum, asset) => sum + asset.total, 0);
      const remaining = Math.max(0, totalTarget - totalContributed);
      const healthPercent =
        totalTarget > 0 ? Math.round((totalContributed / totalTarget) * 100) : 0;
      const overallProgress = this.buildOverallProgress(assets, totalContributed, totalTarget);

      return {
        partners,
        summary: {
          totalEquity: totalTarget,
          paid: totalContributed,
          remaining,
          healthPercent,
        },
        charts: {
          categories: assets.map((asset) => asset.name),
          contributed: assets.map((asset) => asset.paid),
          target: assets.map((asset) => asset.total),
          timelineCategories: overallProgress.categories,
          timelineActual: overallProgress.actual,
          timelineTarget: overallProgress.target,
        },
      };
    })
  );

  constructor(private readonly financeState: FinanceStateService) {}

  ngOnInit(): void {
    this.financeState.loadData();
  }

  formatMoney(value: number): string {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: 0,
    }).format(value);
  }

  private buildOverallProgress(
    assets: Array<{ paid: number; total: number; velocitySeries: number[] }>,
    totalContributed: number,
    totalTarget: number
  ): { categories: string[]; actual: number[]; target: number[] } {
    const timelineMonths = 24;
    const categories = Array.from({ length: timelineMonths }, (_, index) => `M${index + 1}`);
    const elapsedMonths = Math.max(
      1,
      Math.min(
        timelineMonths,
        assets.reduce((max, asset) => Math.max(max, asset.velocitySeries?.length ?? 0), 0)
      )
    );

    const monthlyWeights = Array.from({ length: elapsedMonths }, (_, monthIndex) =>
      assets.reduce((sum, asset) => sum + (asset.velocitySeries?.[monthIndex] ?? 0), 0)
    );
    const weightTotal = monthlyWeights.reduce((sum, value) => sum + value, 0);
    const monthlyPaidHistory = monthlyWeights.map((value) => {
      if (weightTotal > 0) {
        return (value / weightTotal) * totalContributed;
      }

      return totalContributed / elapsedMonths;
    });

    const actual: number[] = [];
    let cumulativePaid = 0;
    for (let month = 0; month < timelineMonths; month += 1) {
      if (month < elapsedMonths) {
        cumulativePaid += monthlyPaidHistory[month];
      } else {
        cumulativePaid += totalContributed / elapsedMonths;
      }

      actual.push(Math.min(totalTarget, Number(cumulativePaid.toFixed(2))));
    }

    const target = Array.from({ length: timelineMonths }, (_, index) =>
      Number((((index + 1) / timelineMonths) * totalTarget).toFixed(2))
    );

    return {
      categories,
      actual,
      target,
    };
  }
}
